import styled from '@emotion/styled';
import { FaFacebook } from '@react-icons/all-files/fa/FaFacebook';
import { FaLinkedin } from '@react-icons/all-files/fa/FaLinkedin';
import { FaTwitter } from '@react-icons/all-files/fa/FaTwitter';
import { useRouter } from 'next/router';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { Image } from '@/components/common/image';
import { Section, WidthConstraint } from '@/components/layout/Section';
import { getTextColour } from '@/lib/colourUtils';
import { bodyMedium } from '@/styles/typography';

import type { HeadingSanityBlockType } from '@/components/common/Heading';
import type { BackgroundColourType } from '@/types/colours';
import type {
  SanityBlogAuthorType,
  SanityBlogCategoryType,
  SanityBlogPostType,
} from '@/types/sanity';

export interface PostPageHeroProps {
  backgroundColour?: BackgroundColourType;
  heading?: HeadingSanityBlockType;
  category?: SanityBlogCategoryType;
  author?: SanityBlogAuthorType;
  publishDate: string;
  datetimeDate: string;
  featuredImage?: SanityBlogPostType['featuredImage'];
  isDecorativeImage?: boolean;
  fallback: SanityBlogPostType['featuredImage'];
}

const PostPageHero = ({
  heading,
  publishDate,
  datetimeDate,
  category,
  author,
  featuredImage,
  backgroundColour,
  fallback,
  isDecorativeImage,
}: PostPageHeroProps) => {
  const { asPath, locale } = useRouter();

  const backgroundColourToken =
    backgroundColour?.token || '--background-warm-strong';

  const titles = heading[0].children;
  let title = '';
  // eslint-disable-next-line no-return-assign
  titles.map((t) => (title = title.concat(t.text)));

  return (
    <Section
      backgroundColour={backgroundColourToken}
      colour={getTextColour(backgroundColourToken)}
      breakout
      verticalPadding="hero"
    >
      <WidthConstraint includePadding={['md', 'lg']}>
        <HeroContainer>
          <HeroContentContainer>
            <Category>
              <BaseLink
                href={`/${locale}/blog/category/${category.slug.current}`}
                showVisited={false}
              >
                {category.name}
              </BaseLink>
            </Category>
            <RichHeading heading={heading} size="headline-md" />
            <AuthorDate>
              <BaseLink
                href={`/${locale}/blog/author/${author.slug.current}`}
                showVisited={false}
              >
                <p>
                  By <b>{author.name}</b>
                </p>
              </BaseLink>
              <span> | </span>
              <div>
                <time dateTime={datetimeDate}>{publishDate}</time>
              </div>
            </AuthorDate>
            <SocialSharing>
              <FacebookShareButton url={asPath} quote={title}>
                <FaFacebook size={45} />
              </FacebookShareButton>
              <TwitterShareButton url={asPath} title={title}>
                <FaTwitter size={45} />
              </TwitterShareButton>
              <LinkedinShareButton url={asPath} title={title} source={title}>
                <FaLinkedin size={45} />
              </LinkedinShareButton>
            </SocialSharing>
          </HeroContentContainer>
          <HeroImageContainer>
            {!!featuredImage?.asset?._ref && (
              <Image
                isDecorativeImage={isDecorativeImage}
                {...featuredImage}
                radius="l"
              />
            )}
            {!featuredImage?.asset?._ref && !!fallback && (
              <Image {...fallback} radius="l" />
            )}
          </HeroImageContainer>
        </HeroContainer>
      </WidthConstraint>
    </Section>
  );
};

const HeroContainer = styled.div`
  b {
    font-weight: var(--font-weight-semi-bold);
    color: inherit;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  ${screen.md} {
    position: relative;
    display: flex;
    flex-direction: row;

    max-width: var(--desktop-max-content-width);
    margin: 0 auto;
  }
  ${screen.lg} {
  }
`;

const HeroContentContainer = styled.div`
  padding: 0 var(--spacing-300);
  width: 100%;

  ${screen.md} {
    width: 50%;
  }

  ${screen.xl} {
    padding-left: 0;
  }
`;
const Category = styled.div`
  text-transform: uppercase;
  margin-bottom: var(--spacing-200);
  position: relative;
  z-index: 1;
  ${screen.md} {
    margin-top: var(--spacing-500);
  }
`;
const AuthorDate = styled.div`
  margin-top: var(--spacing-500);
  margin-bottom: var(--spacing-small);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-x-large);

  span,
  a,
  time {
    ${bodyMedium}
  }
  span {
    display: none;
  }

  ${screen.md} {
    flex-direction: row;
    align-items: center;
    span {
      display: inline;
    }
  }
`;
const SocialSharing = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  margin: 0 0 var(--spacing-300);
  svg {
    padding-right: var(--spacing-200);
  }
`;

const HeroImageContainer = styled.div`
  width: 100%;
  ${screen.md} {
    width: 50%;
    position: relative;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column-reverse;

    img {
      margin-bottom: var(--spacing-300);
      width: 100%;
      border-top-left-radius: var(--radius-m);
      border-bottom-left-radius: var(--radius-m);
    }
  }

  ${screen.lg} {
    position: unset;

    img {
      border-radius: var(--radius-m);
    }
  }
`;

export { PostPageHero };
